// src/hooks/useCompanySettingsData.js
import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getSocket,
} from "../lib/socket";

import {
  patchCompanySettingsCache,
  prefetchCompanySettingsData,
  readCompanySettingsCache,
  subscribeCompanySettingsCache,
} from "../utils/companySettingsCache";

/*
 * --------------------------------------------------------------------------
 * Shared refresh / WebSocket state
 * --------------------------------------------------------------------------
 *
 * This module can be used by several components at the same time:
 *
 * - CompanySettings
 * - CompanyProfile
 * - OperationDetails
 * - ContactInformation
 * - FinancialInformation
 * - PaymentTerms
 * - TaxInformation
 *
 * All hook instances share the same WebSocket listeners.
 */

let socketConsumerCount =
  0;

let sharedSocket =
  null;

let socketRefreshTimer =
  null;

let subscribedCompanyDetailsId =
  null;

const COMPANY_SETTINGS_WS_EVENTS = [
  "company:created",
  "company:updated",
  "companydetails:created",
  "companydetails:updated",
  "companydetails:deleted",
  "companyprofile:changed",
];

const loadCompanySettingsData =
  ({
    force = false,
  } = {}) =>
    prefetchCompanySettingsData({
      force,
    });

/*
 * --------------------------------------------------------------------------
 * WebSocket refresh coalescing
 * --------------------------------------------------------------------------
 *
 * Several backend events may represent the same logical update.
 *
 * Instead of:
 *
 * event -> GET
 * event -> GET
 * event -> GET
 *
 * we do:
 *
 * event ─┐
 * event ─┼─ 80 ms ─► ONE refresh
 * event ─┘
 */

const scheduleSocketRefresh =
  () => {
    if (
      socketRefreshTimer
    ) {
      window.clearTimeout(
        socketRefreshTimer
      );
    }

    socketRefreshTimer =
      window.setTimeout(
        () => {
          socketRefreshTimer =
            null;

          Promise.resolve(
            loadCompanySettingsData(
              {
                force: true,
              }
            )
          ).catch(
            (
              error
            ) => {
              console.warn(
                "[useCompanySettingsData] websocket refresh failed:",
                error
              );
            }
          );
        },
        80
      );
  };

/*
 * --------------------------------------------------------------------------
 * Socket room subscription
 * --------------------------------------------------------------------------
 */

const subscribeSocketRooms =
  () => {
    if (
      !sharedSocket
    ) {
      return;
    }

    sharedSocket.emit(
      "subscribe:companyprofile"
    );

    if (
      subscribedCompanyDetailsId
    ) {
      sharedSocket.emit(
        "subscribe:companydetails",
        {
          id:
            subscribedCompanyDetailsId,
        }
      );
    }
  };

const unsubscribeSocketRooms =
  () => {
    if (
      !sharedSocket
    ) {
      return;
    }

    sharedSocket.emit(
      "unsubscribe:companyprofile"
    );

    if (
      subscribedCompanyDetailsId
    ) {
      sharedSocket.emit(
        "unsubscribe:companydetails",
        {
          id:
            subscribedCompanyDetailsId,
        }
      );
    }
  };

/*
 * --------------------------------------------------------------------------
 * Start global socket listener
 * --------------------------------------------------------------------------
 */

const startCompanySettingsSocket =
  () => {
    if (
      sharedSocket
    ) {
      return;
    }

    sharedSocket =
      getSocket();

    sharedSocket.on(
      "connect",
      subscribeSocketRooms
    );

    sharedSocket.on(
      "reconnect",
      subscribeSocketRooms
    );

    COMPANY_SETTINGS_WS_EVENTS.forEach(
      (
        eventName
      ) => {
        sharedSocket.on(
          eventName,
          scheduleSocketRefresh
        );
      }
    );

    subscribeSocketRooms();
  };

/*
 * --------------------------------------------------------------------------
 * Stop global socket listener
 * --------------------------------------------------------------------------
 */

const stopCompanySettingsSocket =
  () => {
    if (
      !sharedSocket
    ) {
      return;
    }

    if (
      socketRefreshTimer
    ) {
      window.clearTimeout(
        socketRefreshTimer
      );

      socketRefreshTimer =
        null;
    }

    unsubscribeSocketRooms();

    sharedSocket.off(
      "connect",
      subscribeSocketRooms
    );

    sharedSocket.off(
      "reconnect",
      subscribeSocketRooms
    );

    COMPANY_SETTINGS_WS_EVENTS.forEach(
      (
        eventName
      ) => {
        sharedSocket.off(
          eventName,
          scheduleSocketRefresh
        );
      }
    );

    sharedSocket =
      null;

    subscribedCompanyDetailsId =
      null;
  };

/*
 * --------------------------------------------------------------------------
 * Register one hook consumer
 * --------------------------------------------------------------------------
 */

const registerSocketConsumer =
  (
    companyDetailsId
  ) => {
    socketConsumerCount +=
      1;

    if (
      companyDetailsId &&
      companyDetailsId !==
        subscribedCompanyDetailsId
    ) {
      const previousId =
        subscribedCompanyDetailsId;

      subscribedCompanyDetailsId =
        companyDetailsId;

      if (
        sharedSocket &&
        previousId
      ) {
        sharedSocket.emit(
          "unsubscribe:companydetails",
          {
            id:
              previousId,
          }
        );
      }

      if (
        sharedSocket
      ) {
        sharedSocket.emit(
          "subscribe:companydetails",
          {
            id:
              companyDetailsId,
          }
        );
      }
    }

    startCompanySettingsSocket();

    return () => {
      socketConsumerCount =
        Math.max(
          0,
          socketConsumerCount -
            1
        );

      if (
        socketConsumerCount ===
        0
      ) {
        stopCompanySettingsSocket();
      }
    };
  };

export function useCompanySettingsData() {
  const [
    data,
    setData,
  ] = useState(
    () =>
      readCompanySettingsCache()
  );

  const companyDetailsId =
    data?.details?.id ??
    null;

  /*
   * ------------------------------------------------------------------------
   * Cache subscription + stale-while-revalidate
   * ------------------------------------------------------------------------
   *
   * 1. React renders immediately from cache.
   * 2. Subscribe to all future cache changes.
   * 3. Refresh silently in the background.
   */

  useEffect(() => {
    let alive = true;

    const unsubscribe =
      subscribeCompanySettingsCache(
        (
          nextData
        ) => {
          if (
            !alive
          ) {
            return;
          }

          setData(
            nextData ||
              null
          );
        }
      );

    Promise.resolve(
      loadCompanySettingsData()
    )
      .then(
        (
          nextData
        ) => {
          if (
            alive &&
            nextData
          ) {
            setData(
              nextData
            );
          }
        }
      )
      .catch(
        (
          error
        ) => {
          console.warn(
            "[useCompanySettingsData] background refresh failed:",
            error
          );
        }
      );

    return () => {
      alive = false;

      unsubscribe();
    };
  }, []);

  /*
   * ------------------------------------------------------------------------
   * WebSocket
   * ------------------------------------------------------------------------
   *
   * All hook instances share ONE socket listener.
   */

  useEffect(() => {
    return registerSocketConsumer(
      companyDetailsId
    );
  }, [
    companyDetailsId,
  ]);

  /*
   * ------------------------------------------------------------------------
   * Manual refresh
   * ------------------------------------------------------------------------
   */

  const refresh =
    useCallback(
      async () => {
        const nextData =
          await loadCompanySettingsData(
            {
              force: true,
            }
          );

        if (
          nextData
        ) {
          setData(
            nextData
          );
        }

        return nextData;
      },
      []
    );

  /*
   * ------------------------------------------------------------------------
   * Optimistic/local patch
   * ------------------------------------------------------------------------
   *
   * patchCompanySettingsCache must:
   *
   * - update memory immediately
   * - persist cache immediately
   * - notify every subscriber immediately
   *
   * Therefore every mounted Company Settings component changes in the
   * same render cycle.
   */

  const patchSection =
    useCallback(
      (
        section,
        patch
      ) => {
        const nextData =
          patchCompanySettingsCache(
            section,
            patch
          );

        setData(
          nextData
        );

        return nextData;
      },
      []
    );

  return {
    data,
    refresh,
    patchSection,

    hasCachedData:
      Boolean(
        data
      ),
  };
}